/*
 * Copyright (c) CIRROM 2021.
 */

import { IntegratedSystemDetail } from "../../api/integratedsystems";
import { useTheme } from "@mui/material/styles";
import React, { useMemo, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from "react-simple-maps";
import worldTopoJson from "world-atlas/countries-110m.json";

/**
 * The map data ships with the bundle rather than being fetched from a CDN, which is
 * what react-simple-maps' own documentation does. A remote topojson would need a
 * connect-src hole in the Content-Security-Policy, and this component exists in its
 * current form precisely because the previous library forced 'unsafe-eval'.
 */
const WORLD_TOPO_JSON = worldTopoJson as any;

/** Marker radii, matching the [3, 8] range the previous vector map scaled markers over. */
const MIN_MARKER_RADIUS = 3;
const MAX_MARKER_RADIUS = 8;

/** Below this many markers the map opens zoomed in on the first one, as it did before. */
const AUTO_FOCUS_MARKER_LIMIT = 25;
const AUTO_FOCUS_ZOOM = 3;

/**
 * The card this sits in is far wider than it is tall, so the viewBox is shaped to match.
 * An SVG letterboxes when its aspect ratio disagrees with its box, which is what left a
 * band of dead space above the map. geoEquirectangular spans 2*PI*scale across, so
 * deriving the scale from the width runs the map edge to edge and crops the empty polar
 * caps instead of padding the card.
 */
const VIEWBOX_WIDTH = 800;
const VIEWBOX_HEIGHT = 340;
const PROJECTION_SCALE = VIEWBOX_WIDTH / (2 * Math.PI);

/** With nothing plotted there is no focal point, so lean in rather than show dead ocean. */
const NO_MARKER_ZOOM = 1.3;

type ContactLocation = {
  id: number;
  title: string;
  lat: number;
  long: number;
  count: number;
};

const formatCount = (count: number) =>
  new Intl.NumberFormat("en-US").format(count);

/**
 * Linear over the observed range so the busiest location is always the largest dot and
 * the quietest the smallest. A single location, or several with identical counts, has no
 * range to spread across and takes the largest radius rather than an arbitrary midpoint.
 */
const markerRadius = (count: number, counts: number[]) => {
  const min = Math.min(...counts);
  const max = Math.max(...counts);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return MAX_MARKER_RADIUS;
  }
  const ratio = (count - min) / (max - min);
  return MIN_MARKER_RADIUS + ratio * (MAX_MARKER_RADIUS - MIN_MARKER_RADIUS);
};

export const ComplianceMap = React.memo(function ComplianceMap(props: {
  systemDetails: IntegratedSystemDetail;
  onMarkerClick?: (event, index) => void;
  containerStyle?: any;
}) {
  const { containerStyle, onMarkerClick, systemDetails } = props;
  const theme = useTheme();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const locations: ContactLocation[] = useMemo(
    () => systemDetails?.crm_stats?.contact_locations || [],
    [systemDetails]
  );
  const counts = useMemo(() => locations.map((l) => l.count), [locations]);

  // A handful of locations is unreadable at world scale, so open focused on the first
  // one; a full spread stays zoomed out where the distribution is the point.
  const initialView = useMemo(() => {
    if (locations.length === 0) {
      return { center: [0, 0] as [number, number], zoom: NO_MARKER_ZOOM };
    }
    if (locations.length < AUTO_FOCUS_MARKER_LIMIT) {
      return {
        center: [locations[0].long, locations[0].lat] as [number, number],
        zoom: AUTO_FOCUS_ZOOM,
      };
    }
    return { center: [0, 0] as [number, number], zoom: 1 };
  }, [locations]);

  const regionFill = theme.palette.mode === "dark" ? "#666f7d" : "#e2eaef";
  const selectable = Boolean(onMarkerClick);

  return (
    <ComposableMap
      projection="geoEquirectangular"
      width={VIEWBOX_WIDTH}
      height={VIEWBOX_HEIGHT}
      projectionConfig={{ scale: PROJECTION_SCALE }}
      style={{
        width: "100%",
        height: "100%",
        display: "block",
        backgroundColor: theme.palette.background.paper,
        ...(containerStyle || {}),
      }}
    >
      <ZoomableGroup center={initialView.center} zoom={initialView.zoom}>
        <Geographies geography={WORLD_TOPO_JSON}>
          {({ geographies }) =>
            geographies.map((geography) => (
              <Geography
                key={geography.rsmKey}
                geography={geography}
                style={{
                  default: { fill: regionFill, outline: "none" },
                  hover: { fill: regionFill, outline: "none" },
                  pressed: { fill: regionFill, outline: "none" },
                }}
              />
            ))
          }
        </Geographies>

        {locations.map((location, index) => (
          <Marker
            key={`${location.id}-${index}`}
            coordinates={[location.long, location.lat]}
            onClick={(event) => {
              if (!selectable) {
                return;
              }
              setSelectedIndex(index);
              onMarkerClick(event, index);
            }}
          >
            <Tooltip
              title={`${location.title} (${formatCount(location.count)})`}
            >
              <circle
                r={markerRadius(location.count, counts)}
                fill={theme.palette.secondary.main}
                fillOpacity={1}
                stroke="#fff"
                // A wide, mostly transparent stroke reads as a halo around the dot; the
                // selected marker tightens it so the choice stays visible while a
                // breakdown loads beside the map.
                strokeWidth={selectedIndex === index ? 1.5 : 7}
                strokeOpacity={selectedIndex === index ? 1 : 0.4}
                style={{ cursor: selectable ? "pointer" : "default" }}
              />
            </Tooltip>
          </Marker>
        ))}
      </ZoomableGroup>
    </ComposableMap>
  );
});
